import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * A surface's claim on the top bar.
 *
 * Selection replaces the bar entirely rather than adding a strip under it —
 * the app's own convention, and the reason is that a selection is a MODE:
 * while it exists, what the bar offers is different, and stacking two bars
 * would say the opposite.
 *
 * The bar lives in the shell and the claim comes from a route, so this is the
 * seam between them, exactly as the holder context is for writing.
 */
const OverrideContext = createContext<{
  bar: ReactNode | null;
  setBar: (bar: ReactNode | null) => void;
}>({ bar: null, setBar: () => {} });

export function OverrideBarProvider({ children }: { children: ReactNode }) {
  const [bar, setBar] = useState<ReactNode | null>(null);
  const value = useMemo(() => ({ bar, setBar }), [bar]);
  return <OverrideContext.Provider value={value}>{children}</OverrideContext.Provider>;
}

export function useOverrideBar(): ReactNode | null {
  return useContext(OverrideContext).bar;
}

/** Claims the bar while `bar` is non-null, and releases it on the way out —
 *  a stale claim would leave a selection bar over a page with no selection. */
export function useProvideOverrideBar(bar: ReactNode | null): void {
  const { setBar } = useContext(OverrideContext);
  useEffect(() => {
    setBar(bar);
    return () => setBar(null);
  }, [setBar, bar]);
}
